import { format } from 'date-fns';
import { aud } from '@/utils/format';
import {
  artistArrearsAud,
  cashLedgerFor,
  dayExpectedPayouts,
  dayPayouts,
  isCommitted,
  payoutsStillDueAud,
  studioArrearsAud,
} from '@/domain/payoutFlow';
import { bookingPriceAud } from '@/domain/pricing';
import { useBrainStore } from '@/state/brainStore';
import { useSimulationStore } from '@/state/simulationStore';
import { seedNow } from '@/domain/seedContext';
import { depositPaidAud } from '@/utils/bookingSummary';
import { CLOCKOFF_HOUR, isEveningStartIso } from '@/domain/paymentTiming';
import { dayKey } from '@/utils/studioDay';
import type { StudioId } from '@/domain/types';

function nowIso(): string {
  return seedNow() ?? useSimulationStore.getState().clockIso;
}

/**
 * Cash-vs-card steering (Prompt E1). Artists are paid IN CASH at their finish
 * time, and cash is deliberately front-loaded onto early clients so the envelope
 * is ready. This module answers one question at checkout: should the manager ask
 * this client for cash, or is card fine?
 *
 * `// ASSUMPTION:` the exact steering algorithm is an open client question. The
 * heuristic here is the demo stand-in: compare the payouts still due today
 * against WORKING CASH (cash taken − payouts handed out). The opening float is
 * excluded on purpose — it is a sealed emergency reserve, not spending money.
 */

export type PaymentSteerMethod = 'cash' | 'card';

export interface PaymentSteer {
  method: PaymentSteerMethod;
  workingCashAud: number;
  payoutsStillDueAud: number;
  /** How far short working cash falls of the payouts due (0 when covered). */
  shortfallAud: number;
  /** The earliest unpaid finisher driving the call, for the reasoning line. */
  nextFinish?: { artistName: string; finishIso?: string; envelopeAud: number };
  /** One-line human reasoning, e.g. the studio's "Asher finishes ~4pm…" line. */
  reasoning: string;
}

/**
 * Pure core: short on working cash → ask for cash; covered → card is fine.
 * Kept separate so the decision rule is trivially testable (flip the inputs,
 * flip the answer) without touching the store.
 */
export function steerPayment(workingCash: number, payoutsDue: number): PaymentSteerMethod {
  return payoutsDue > workingCash ? 'cash' : 'card';
}

/** Live steering for a studio's day, reading takings, payouts and the ledger. */
export function recommendPaymentMethod(studioId: StudioId, dayIso: string): PaymentSteer {
  const now = nowIso();
  // Cash collected SO FAR today (by the clock), net of payouts already handed out.
  // Time-phased so the steer front-loads: the morning honestly reads what has
  // actually been taken (little), so it asks for cash to build the till, easing to
  // card once enough is collected to cover the day's still-owed payouts.
  const working = cashInByTime(studioId, dayIso, now) - payoutsHandedByTime(studioId, dayIso, now);
  const due = payoutsStillDueAud(studioId, dayIso);
  const method = steerPayment(working, due);
  const shortfallAud = Math.max(0, due - working);

  // The reasoning names the earliest-finishing artist who still needs paying —
  // the one the cash has to be ready for first. Reads the committed set so the
  // reasoning line matches the forward-looking due total above.
  const next = dayExpectedPayouts(studioId, dayIso)
    .filter((p) => p.outstandingAud > 0)
    .sort((a, b) => (a.expectedFinishIso ?? '~').localeCompare(b.expectedFinishIso ?? '~'))[0];

  let reasoning: string;
  if (!next) {
    reasoning = `All payouts covered · cash on hand ${aud(working)}. Card is fine.`;
  } else {
    // Prefer the actual finish; fall back to the projected one so the line still
    // reads "finishes ~4pm" while the artist is mid-session.
    const finishAt = next.finishIso ?? next.expectedFinishIso;
    const when = finishAt ? `finishes ~${format(new Date(finishAt), 'h:mmaaa')}` : 'still in the chair';
    reasoning =
      method === 'cash'
        ? `${next.artistName} ${when} · envelope needs ${aud(next.outstandingAud)} · cash on hand ${aud(working)}. Ask for cash.`
        : `${next.artistName} ${when} · envelope ${aud(next.outstandingAud)} covered by ${aud(working)} on hand. Card is fine.`;
  }

  return {
    method,
    workingCashAud: working,
    payoutsStillDueAud: due,
    shortfallAud,
    nextFinish: next
      ? { artistName: next.artistName, finishIso: next.finishIso, envelopeAud: next.outstandingAud }
      : undefined,
    reasoning,
  };
}

/** Per-client cash/card steer for the floor. The studio front-loads cash to cover the day's
 * committed payouts: order the day's committed-unpaid clients by slot; a client is asked for CASH
 * while the till (cash already taken from finished sessions + the cash expected from EARLIER
 * committed clients) has not yet reached the payouts still due. Once earlier clients have
 * front-loaded enough, later clients can pay CARD. Read-only. */
export function recommendForClient(studioId: StudioId, dayIso: string, bookingId: string): PaymentSteerMethod {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const due = payoutsStillDueAud(studioId, dayIso);
  const closeIso = `${day}T23:59:00+10:00`;
  const alreadyCollected = cashInByTime(studioId, dayIso, closeIso); // cash from already-paid (finished) sessions
  const unpaid = Object.values(brain.bookings)
    .filter((b) => b.studioId === studioId && dayKey(b.start) === day && isCommitted(b) && !brain.salePayments[`sale-${b.id}`])
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
  let cumBefore = 0;
  for (const b of unpaid) {
    if (b.id === bookingId) break;
    const artist = brain.artists[b.artistId];
    if (!artist) continue;
    const price = bookingPriceAud(b, artist);
    const dep = b.depositId ? depositPaidAud(brain.deposits[b.depositId]) : 0;
    cumBefore += Math.max(0, price - dep);
  }
  return alreadyCollected + cumBefore < due ? 'cash' : 'card';
}

/** Projected cash position at the 6pm close: the studio front-loads CASH to pay artists in cash, so
 * the drawer should net to roughly zero. cashIn = cash already taken (finished cash sales) + the
 * balances of committed clients the steer sends to cash; payouts = the day's committed envelopes
 * (paid in cash at close); leftover = cashIn - payouts (aim ~0, float untouched). Read-only. */
export function projectedCloseCash(studioId: StudioId, dayIso: string): { cashInAud: number; payoutsAud: number; leftoverAud: number } {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const payoutsAud = dayExpectedPayouts(studioId, dayIso).reduce((n, p) => n + p.envelopeAud, 0);
  const alreadyCash = cashInByTime(studioId, dayIso, `${day}T23:59:00+10:00`);
  let cashSteered = 0;
  for (const b of Object.values(brain.bookings)) {
    if (b.studioId !== studioId || dayKey(b.start) !== day || !isCommitted(b)) continue;
    if (brain.salePayments[`sale-${b.id}`]) continue; // already paid
    if (recommendForClient(studioId, dayIso, b.id) !== 'cash') continue;
    const artist = brain.artists[b.artistId];
    if (!artist) continue;
    const dep = b.depositId ? depositPaidAud(brain.deposits[b.depositId]) : 0;
    cashSteered += Math.max(0, bookingPriceAud(b, artist) - dep);
  }
  const cashInAud = alreadyCash + cashSteered;
  return { cashInAud, payoutsAud, leftoverAud: cashInAud - payoutsAud };
}

/**
 * Time-phased CASH by `iso`: cash already in the till by then, plus the balances
 * of unpaid clients the steer sends to cash whose money lands by then.
 *
 * The difference from `expectedCashByTime` matters and is easy to miss.
 * `expectedCashByTime` answers "if every remaining client paid cash, how much
 * would be in by then" — the ceiling used to judge whether a shortfall is
 * genuine or an intraday artefact. This answers "how much cash do we actually
 * expect by then, given who we are asking for cash", so it is the figure that
 * belongs in a chain ending at `projectedDayMoney().cashAud`. Using the former
 * in that chain reads as a contradiction: a by-6pm figure larger than the
 * whole-day predicted cash sitting directly above it.
 *
 * Cash-landing times follow `expectedCashByTime`: a daytime session settles at
 * checkout, an evening session is collected at the 6pm clock-off. Read-only.
 */
export function projectedCashByTime(studioId: StudioId, dayIso: string, iso: string): number {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const cutoff = new Date(iso).getTime();
  const clockoff = new Date(`${day}T${String(CLOCKOFF_HOUR).padStart(2, '0')}:00:00+10:00`).getTime();
  let steered = 0;
  for (const b of Object.values(brain.bookings)) {
    if (b.studioId !== studioId || dayKey(b.start) !== day || !isCommitted(b)) continue;
    if (brain.salePayments[`sale-${b.id}`]) continue; // already paid -> in actual cash
    if (recommendForClient(studioId, dayIso, b.id) !== 'cash') continue;
    const artist = brain.artists[b.artistId];
    if (!artist) continue;
    const cashLandsAt = isEveningStartIso(b.start)
      ? clockoff
      : new Date(
          b.checkedOutAt ?? new Date(new Date(b.start).getTime() + b.durationMin * 60_000).toISOString(),
        ).getTime();
    if (cashLandsAt > cutoff) continue;
    const dep = b.depositId ? depositPaidAud(brain.deposits[b.depositId]) : 0;
    steered += Math.max(0, bookingPriceAud(b, artist) - dep);
  }
  return cashInByTime(studioId, dayIso, iso) + steered;
}

/** The whole day's money, split by where it lands. */
export interface DayMoney {
  /** Committed sessions x their price. The day's gross, merch excluded. */
  bookedAud: number;
  /** Deposits already taken against today's sessions — banked before today. */
  depositsBankedAud: number;
  /** What is left to collect today: booked less the deposits already held. */
  balancesAud: number;
  /** Cash already in the till from finished sales. */
  cashCollectedAud: number;
  /** Balances the steer will ask for in cash. */
  cashPendingAud: number;
  /** Total cash across the day: collected + pending. */
  cashAud: number;
  /** Card/transfer already taken from finished sales. */
  bankCollectedAud: number;
  /** Balances the steer is happy to take on card. */
  bankPendingAud: number;
  /** Total to the bank: deposits + card collected + card pending. */
  bankAud: number;
  /** Envelopes across every committed session — what the whole day will owe. */
  toArtistsDayAud: number;
  /** Envelopes for sessions under way or done — what is owed on work so far. */
  toArtistsNowAud: number;
  /**
   * Envelopes carried from EARLIER days, still owed whole. Deliberately its own
   * figure rather than folded into `toArtistsDayAud`: arrears are not today's
   * commission, and the day's conservation identities (booked === cash + bank,
   * studio share === bank + drawer surplus) are about today's client money.
   * They do come out of today's drawer, which is why `drawerAtCloseAud` nets them.
   */
  arrearsAud: number;
  /** The studio's half of the booked day. */
  studioShareAud: number;
  /** Float on hand at open. */
  floatAud: number;
  /** Float + cash taken − envelopes paid out (today's and any carried), at the 6pm close. */
  drawerAtCloseAud: number;
  /** Drawer at close less the sealed float — the cash surplus that stays behind. */
  drawerSurplusAud: number;
}

/**
 * Where the day's money actually goes — the conservation view behind the cash
 * tiles (27 Jul).
 *
 * `projectedCloseCash` answers only "how much cash?", which leaves the drawer
 * netting to roughly zero with no visible explanation of where the rest went.
 * The rest goes to the BANK: the deposits taken before today, plus every balance
 * the steer is happy to take on card. Without that leg the money appears to
 * vanish, which is what made the maths unreadable.
 *
 * The invariant worth checking on any day: `bookedAud === cashAud + bankAud`,
 * and the studio's share equals what reaches the bank plus the cash surplus left
 * in the drawer.
 *
 * ASSUMPTION: deposits are treated as bank money. They are taken by link/card/
 * transfer days earlier and have already settled, so they never pass through
 * today's till. A deposit taken in cash on the day itself would break this.
 *
 * Read-only, merch excluded (the studio keeps merch whole, it never splits).
 */
export function projectedDayMoney(studioId: StudioId, dayIso: string): DayMoney {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const closeIso = `${day}T23:59:00+10:00`;

  let bookedAud = 0;
  let depositsBankedAud = 0;
  let cashPendingAud = 0;
  let bankPendingAud = 0;

  for (const b of Object.values(brain.bookings)) {
    if (b.studioId !== studioId || dayKey(b.start) !== day || !isCommitted(b)) continue;
    const artist = brain.artists[b.artistId];
    if (!artist) continue;
    const price = bookingPriceAud(b, artist);
    const dep = b.depositId ? depositPaidAud(brain.deposits[b.depositId]) : 0;
    bookedAud += price;
    depositsBankedAud += Math.min(dep, price);
    if (brain.salePayments[`sale-${b.id}`]) continue; // balance already collected
    const balance = Math.max(0, price - dep);
    if (recommendForClient(studioId, dayIso, b.id) === 'cash') cashPendingAud += balance;
    else bankPendingAud += balance;
  }

  // Split what has already been rung up: cash to the till, the remainder to the
  // bank. A mixed sale contributes to both, which is why this reads cashInAud
  // rather than switching on `method`.
  const cashCollectedAud = cashInByTime(studioId, dayIso, closeIso);
  let bankCollectedAud = 0;
  for (const s of Object.values(brain.salePayments)) {
    if (s.studioId !== studioId || dayKey(s.takenAt) !== day) continue;
    bankCollectedAud += Math.max(0, s.balanceAud - s.cashInAud);
  }

  const cashAud = cashCollectedAud + cashPendingAud;
  const bankAud = depositsBankedAud + bankCollectedAud + bankPendingAud;
  const toArtistsDayAud = dayExpectedPayouts(studioId, dayIso).reduce((n, p) => n + p.envelopeAud, 0);
  const toArtistsNowAud = dayPayouts(studioId, dayIso).reduce((n, p) => n + p.envelopeAud, 0);
  const arrearsAud = studioArrearsAud(studioId, dayIso);
  const floatAud = cashLedgerFor(studioId, dayIso).openingFloatAud;

  return {
    bookedAud,
    depositsBankedAud,
    balancesAud: bookedAud - depositsBankedAud,
    cashCollectedAud,
    cashPendingAud,
    cashAud,
    bankCollectedAud,
    bankPendingAud,
    bankAud,
    toArtistsDayAud,
    toArtistsNowAud,
    arrearsAud,
    studioShareAud: bookedAud - toArtistsDayAud,
    floatAud,
    // Carried envelopes are paid out of TODAY's drawer, so the close position has
    // to net them: on the morning after a deferral the drawer really does hold
    // less than today's own maths suggests.
    drawerAtCloseAud: floatAud + cashAud - toArtistsDayAud - arrearsAud,
    drawerSurplusAud: cashAud - toArtistsDayAud,
  };
}

/**
 * The steer with the CURRENT in-progress sale projected on top (D7). Keeps
 * steerPayment/recommendPaymentMethod pure; layers the not-yet-committed sale's
 * cash so choosing cash/card at checkout moves the figure live. Only a cash (or
 * mixed) sale adds working cash; card adds none.
 */
export function projectedRecommendation(
  studioId: StudioId,
  dayIso: string,
  inProgress: { method: PaymentSteerMethod | 'mixed'; collectNowAud: number },
): PaymentSteer {
  const base = recommendPaymentMethod(studioId, dayIso);
  const projectedCash = inProgress.method === 'card' ? 0 : Math.max(0, inProgress.collectNowAud);
  const working = base.workingCashAud + projectedCash;
  const due = base.payoutsStillDueAud;
  const method = steerPayment(working, due);
  return {
    ...base,
    method,
    workingCashAud: working,
    shortfallAud: Math.max(0, due - working),
    reasoning:
      method === 'cash'
        ? `${aud(due)} still to pay out, ${aud(working)} on hand (incl. this sale). Ask for cash.`
        : `${aud(due)} to pay out covered by ${aud(working)} on hand (incl. this sale). Card is fine.`,
  };
}

/**
 * Net cash taken to the till by time `iso` (sum of SalePayment.cashInAud,
 * takenAt <= iso). Compared as actual instants, not raw strings - seeded
 * takings carry a `+10:00` offset while a rostered shift end (`resolveShiftEnd`)
 * is a `Z`-format string, and those two formats do not sort correctly as text.
 */
export function cashInByTime(studioId: StudioId, dayIso: string, iso: string): number {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const cutoff = new Date(iso).getTime();
  return Object.values(brain.salePayments)
    .filter((s) => s.studioId === studioId && dayKey(s.takenAt) === day && new Date(s.takenAt).getTime() <= cutoff)
    .reduce((sum, s) => sum + s.cashInAud, 0);
}

/**
 * Payouts handed out by time `iso` (sum of PayoutRecord.amountPaidAud, signedAt
 * <= iso). Compared as instants for the same reason as `cashInByTime`.
 */
function payoutsHandedByTime(studioId: StudioId, dayIso: string, iso: string): number {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const cutoff = new Date(iso).getTime();
  return Object.values(brain.payoutRecords)
    .filter(
      (r) => r.studioId === studioId && r.dayIso === day && r.signedAt !== undefined && new Date(r.signedAt).getTime() <= cutoff,
    )
    .reduce((sum, r) => sum + r.amountPaidAud, 0);
}

export interface ArtistCover {
  artistId: string;
  artistName: string;
  shiftEndIso?: string;
  outstandingAud: number;
  /** Carried from earlier days, owed to this artist before today's envelope. */
  arrearsAud: number;
  cumulativeDueAud: number;
  cashInHandAud: number;
  expectedByShiftEndAud: number;
  covered: boolean;
  shortfallAud: number;
  signed: boolean; // fully paid, drops to the bottom
}

/**
 * Expected cash landed by `iso` (D7 forecast): actual cash in by then PLUS the
 * expected balance of unpaid, commissionable bookings due to finish before then.
 * A booking drops out the moment it is paid (a SalePayment exists); if it was
 * paid by card it then contributes $0 actual cash, so the forecast falls to
 * reality. READ-only.
 * // ASSUMPTION: expected client cash = session price - deposit on file, and
 * every unpaid session finishing before `iso` is assumed to pay. The payment
 * guard (D5) handles a cash-flagged client who chooses card.
 */
export function expectedCashByTime(studioId: StudioId, dayIso: string, iso: string): number {
  const brain = useBrainStore.getState();
  const day = dayKey(dayIso);
  const cutoff = new Date(iso).getTime();
  const clockoff = new Date(
    `${day}T${String(CLOCKOFF_HOUR).padStart(2, '0')}:00:00+10:00`,
  ).getTime();
  let future = 0;
  for (const b of Object.values(brain.bookings)) {
    if (b.studioId !== studioId || dayKey(b.start) !== day) continue;
    if (b.status === 'cancelled' || b.status === 'no-show') continue;
    // Only sessions that are actually scheduled to happen today feed the forecast:
    // a confirmed (arriving), checked-in or in-chair client is expected to pay; a
    // held / pending-deposit booking is not committed and must not inflate cover.
    if (b.status !== 'confirmed' && b.status !== 'checked-in' && b.status !== 'in-chair') continue;
    if (brain.salePayments[`sale-${b.id}`]) continue; // already paid -> in actual cash
    // When does this booking's cash land? Daytime sessions settle at checkout.
    // An evening session (start >= 6pm) is collected at the 6pm clock-off, before
    // it starts (H), so its cash is available from 6pm - not at a late finish that
    // would otherwise read as a phantom shortfall against the 6pm payout.
    // ASSUMPTION: evening cash is collected at check-in around 6pm.
    const cashLandsAt = isEveningStartIso(b.start)
      ? clockoff
      : new Date(b.checkedOutAt ?? new Date(new Date(b.start).getTime() + b.durationMin * 60_000).toISOString()).getTime();
    if (cashLandsAt > cutoff) continue;
    const artist = brain.artists[b.artistId];
    if (!artist) continue;
    const price = bookingPriceAud(b, artist);
    const deposit = b.depositId ? depositPaidAud(brain.deposits[b.depositId]) : 0;
    future += Math.max(0, price - deposit);
  }
  return cashInByTime(studioId, dayIso, iso) + future;
}

/**
 * Time-phased cash cover (D7). Walks artists in shift-end order; at each unpaid
 * artist's finish compares the day's forecast cash by then vs the cumulative
 * outstanding envelopes up to and including them. Signed (fully paid) artists
 * drop to the bottom. READ-only: reads dayExpectedPayouts (the committed set) +
 * SalePayment timing.
 *
 * The cover verdict is forecast-vs-forecast: cumulative EXPECTED payouts due
 * against the EXPECTED cash landed by that finish (actual cash so far plus the
 * balances of unpaid committed clients due to pay before then, net of payouts
 * already handed out). So a red card means the day's own takings will not cover
 * that finish even by close - a genuine gap - rather than an intraday timing
 * artefact of comparing expected-out against actual-in. `cashInHandAud` stays on
 * the row as the informational actual-cash-so-far figure; it no longer drives the
 * verdict.
 */
export function dayCoverTimeline(studioId: StudioId, dayIso: string): ArtistCover[] {
  const payouts = dayExpectedPayouts(studioId, dayIso); // committed set, shift-end ordered (D6)
  const unpaid = payouts.filter((p) => p.outstandingAud > 0);
  const paid = payouts.filter((p) => p.outstandingAud <= 0);

  let cumulativeDue = 0;
  const covered: ArtistCover[] = unpaid.map((p) => {
    // Anything carried from earlier days is owed to this artist BEFORE today's
    // envelope, and falls due at the same finish, so it rides on their own rung.
    // Without it the ladder reads green on the morning after a deferral while the
    // drawer is genuinely short.
    const arrearsAud = artistArrearsAud(studioId, p.artistId, dayIso);
    cumulativeDue += p.outstandingAud + arrearsAud;
    const when = p.finishIso ?? p.expectedFinishIso ?? dayIso;
    const handedByThen = payoutsHandedByTime(studioId, dayIso, when);
    // Actual cash so far, net of payouts handed out - informational only now.
    const cashInHand = cashInByTime(studioId, dayIso, when) - handedByThen;
    // The day's forecast cash by this finish (actual + unpaid balances due by
    // then), net of payouts handed out - this drives the cover verdict.
    const expectedByShiftEndAud = expectedCashByTime(studioId, dayIso, when) - handedByThen;
    const shortfallAud = Math.max(0, cumulativeDue - expectedByShiftEndAud);
    return {
      artistId: p.artistId,
      artistName: p.artistName,
      shiftEndIso: p.expectedFinishIso,
      outstandingAud: p.outstandingAud,
      arrearsAud,
      cumulativeDueAud: cumulativeDue,
      cashInHandAud: cashInHand,
      expectedByShiftEndAud,
      covered: shortfallAud === 0,
      shortfallAud,
      signed: false,
    };
  });

  const settled: ArtistCover[] = paid.map((p) => ({
    artistId: p.artistId, artistName: p.artistName, shiftEndIso: p.expectedFinishIso,
    outstandingAud: 0, arrearsAud: 0, cumulativeDueAud: 0, cashInHandAud: 0, expectedByShiftEndAud: 0, covered: true, shortfallAud: 0, signed: true,
  }));

  return [...covered, ...settled];
}
